import { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreVert, WarningAmberOutlined } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Skeleton,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ackAlert, getAlerts } from '../../../../services/alerts';
import { fetchRecord } from '../../../../services/records';
import OverviewCard from './OverviewCard';

const alertColors = {
    FALL: '#FF5252',
    HR_OVER_LIMIT: '#FF5252',
    HR_LOW_EFFORT: '#FFAD27'
};

const AlertsOverviewCard = ({ patientUuid, onViewAlerts }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [openingSession, setOpeningSession] = useState(false);
    const [actionError, setActionError] = useState(false);

    const alertsQuery = useQuery({
        queryKey: ['patient-alerts-summary', patientUuid],
        queryFn: () =>
            getAlerts({
                patient: patientUuid,
                ack_pending: true,
                pageLimit: 4,
                qi: null,
                df: null
            }),
        enabled: Boolean(patientUuid),
        staleTime: 60 * 1000,
        refetchOnMount: 'always'
    });

    const acknowledgeMutation = useMutation({
        mutationFn: ackAlert,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['patient-alerts-summary', patientUuid]
            });
            queryClient.invalidateQueries({ queryKey: ['alerts'] });
        },
        onError: () => setActionError(true)
    });

    const alerts = Array.isArray(alertsQuery.data?.data) ? alertsQuery.data.data : [];
    const closeMenu = () => setAnchorEl(null);

    const openMenu = (event, alert) => {
        setSelectedAlert(alert);
        setAnchorEl(event.currentTarget);
        setActionError(false);
    };

    const viewSession = async () => {
        if (!selectedAlert?.session) return;

        closeMenu();
        setOpeningSession(true);
        setActionError(false);

        try {
            const { data: record } = await fetchRecord(selectedAlert.session);
            navigate('/users/user/record', {
                state: {
                    record,
                    from: location,
                    seeUserState: { selected: 0, expanded: false }
                }
            });
        } catch {
            setActionError(true);
        } finally {
            setOpeningSession(false);
        }
    };

    const confirmAcknowledgement = () => {
        closeMenu();
        setConfirmOpen(true);
    };

    const acknowledgeAlert = async () => {
        if (!selectedAlert) return;

        setConfirmOpen(false);
        setActionError(false);
        await acknowledgeMutation.mutateAsync(selectedAlert.uuid).catch(() => undefined);
    };

    return (
        <OverviewCard
            title={t('userPage.overview.alerts')}
            icon={<WarningAmberOutlined fontSize="small" sx={{ color: '#FFAD27' }} />}
            height={310}
            minHeight={310}
            contentInset={4}
        >
            {alertsQuery.isPending ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateRows: 'repeat(4, minmax(34px, 1fr))',
                        flex: 1
                    }}
                >
                    {[1, 2, 3, 4].map((item) => (
                        <Skeleton key={item} height={29} />
                    ))}
                </Box>
            ) : alertsQuery.isError ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5
                    }}
                >
                    <Typography color="error">
                        {t('userPage.overview.alertList.loadError')}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => alertsQuery.refetch()}
                    >
                        {t('userPage.overview.measurements.retry')}
                    </Button>
                </Box>
            ) : alerts.length === 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t('userPage.overview.alertList.none')}
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateRows: 'repeat(4, minmax(34px, 1fr))',
                        flex: 1
                    }}
                >
                    {alerts.map((alert, index) => (
                        <Box
                            key={alert.uuid}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '16px 170px minmax(0, 1fr) 32px',
                                alignItems: 'center',
                                gap: 1,
                                minHeight: 34,
                                borderTop: index === 0 ? 0 : '1px solid #E0E5E9'
                            }}
                        >
                            <Box
                                aria-hidden="true"
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: alertColors[alert.type] || '#FFAD27'
                                }}
                            />
                            <Typography
                                variant="caption"
                                noWrap
                                sx={{ color: 'text.secondary', fontWeight: 600 }}
                            >
                                {alert.ts && dayjs(alert.ts).isValid()
                                    ? dayjs(alert.ts).format('DD/MM/YYYY - HH:mm')
                                    : '—'}
                            </Typography>
                            <Typography
                                variant="caption"
                                noWrap
                                title={t(`alerts.types.${alert.type}`, {
                                    defaultValue: alert.type
                                })}
                                sx={{ color: '#263238', fontWeight: 700 }}
                            >
                                {t(`alerts.types.${alert.type}`, {
                                    defaultValue: alert.type
                                })}
                            </Typography>
                            {openingSession && selectedAlert?.uuid === alert.uuid ? (
                                <CircularProgress
                                    size={18}
                                    sx={{ justifySelf: 'center' }}
                                />
                            ) : (
                                <IconButton
                                    size="small"
                                    aria-label={t('userPage.overview.alertList.actions')}
                                    onClick={(event) => openMenu(event, alert)}
                                >
                                    <MoreVert fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            {actionError && (
                <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                    {t('userPage.overview.alertList.actionError')}
                </Alert>
            )}

            <Button
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={onViewAlerts}
                sx={{
                    alignSelf: 'flex-end',
                    flexShrink: 0,
                    mt: 1,
                    mr: -4,
                    px: 0.5,
                    fontWeight: 600
                }}
            >
                {t('userPage.overview.viewAlerts')}
            </Button>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                <MenuItem onClick={viewSession} disabled={!selectedAlert?.session}>
                    {t('alerts.viewSession')}
                </MenuItem>
                <MenuItem onClick={confirmAcknowledgement}>
                    {t('alerts.markAsAnalyzed')}
                </MenuItem>
            </Menu>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>{t('alerts.confirmAnalyzeDialog')}</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={acknowledgeAlert}
                        color="primary"
                        disabled={acknowledgeMutation.isPending}
                        autoFocus
                    >
                        {t('common.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </OverviewCard>
    );
};

AlertsOverviewCard.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    onViewAlerts: PropTypes.func.isRequired
};

export default AlertsOverviewCard;
